import { useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../i18n';

type Option = {
	value: string;
	label: string;
};

type SearchableMultiSelectProps = {
	id: string;
	label: string;
	placeholder: string;
	options: Option[];
	selectedValues: string[];
	onToggle: (value: string) => void;
	emptyText?: string;
};

export default function SearchableMultiSelect({
	id,
	label,
	placeholder,
	options,
	selectedValues,
	onToggle,
	emptyText
}: SearchableMultiSelectProps) {
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const [isOpen, setIsOpen] = useState(false);
	const [query, setQuery] = useState('');
	const rootRef = useRef<HTMLDivElement | null>(null);
	const resolvedEmptyText = emptyText ?? copy('No options available.', 'Keine Optionen verfügbar.', 'Nema dostupnih opcija.');

	useEffect(() => {
		if (!isOpen) return;
		const onClickOutside = (event: MouseEvent) => {
			if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', onClickOutside);
		return () => document.removeEventListener('mousedown', onClickOutside);
	}, [isOpen]);

	const filteredOptions = useMemo(() => {
		const normalized = query.trim().toLowerCase();
		if (!normalized) return options;
		return options.filter((option) => option.label.toLowerCase().includes(normalized));
	}, [options, query]);

	const selectedLabels = useMemo(() => {
		if (selectedValues.length === 0) return '';
		const labels = selectedValues
			.map((selected) => options.find((option) => option.value === selected)?.label ?? selected)
			.filter(Boolean);
		return labels.join(', ');
	}, [options, selectedValues]);

	return (
		<div className="searchable-multiselect" ref={rootRef}>
			<label htmlFor={id} className="mb-2 text-muted text-uppercase small d-block">
				{label}
			</label>
			<button
				id={id}
				type="button"
				className={`searchable-multiselect__trigger ${isOpen ? 'is-open' : ''}`}
				onClick={() => setIsOpen((prev) => !prev)}
				aria-expanded={isOpen}
			>
				<span className={selectedValues.length > 0 ? 'text-dark' : 'text-muted'}>
					{selectedValues.length > 0 ? selectedLabels : placeholder}
				</span>
				<span className="d-inline-flex align-items-center gap-2">
					{selectedValues.length > 0 && (
						<span className="badge rounded-pill text-bg-success">{selectedValues.length}</span>
					)}
					<i className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
				</span>
			</button>
			{isOpen && (
				<div className="searchable-multiselect__menu">
					<div className="input-group input-group-sm mb-2">
						<span className="input-group-text bg-white border-end-0">
							<i className="bi bi-search text-muted"></i>
						</span>
						<input
							type="text"
							className="form-control border-start-0"
							placeholder={copy('Search...', 'Suchen...', 'Pretraži...')}
							value={query}
							onChange={(event) => setQuery(event.target.value)}
						/>
					</div>
					<div className="searchable-multiselect__options">
						{filteredOptions.length === 0 ? (
							<p className="text-muted small mb-0 px-1 py-2">{resolvedEmptyText}</p>
						) : (
							filteredOptions.map((option) => {
								const checked = selectedValues.includes(option.value);
								return (
									<label key={option.value} className="searchable-multiselect__option">
										<input
											className="form-check-input me-2"
											type="checkbox"
											checked={checked}
											onChange={() => onToggle(option.value)}
										/>
										<span>{option.label}</span>
									</label>
								);
							})
						)}
					</div>
				</div>
			)}
		</div>
	);
}
